/**
 * MobileInterviewsList
 *
 * Mobile card-based replacement for the PrimeReact DataTable in VentrView.
 * Shows each interview as a touch-friendly card with collab name, team,
 * interview type, planned date, and status chip.
 * Supports search, filter chips (status + type), pagination, and tap-to-open.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 20;

/* Status colors matching VentrSlice */
const STATUS_COLORS = {
  upcoming: "#3c1053",
  awaiting_completion: "#f2a900",
  awaiting_scheduling: "#005670",
  ongoing: "#041e42",
  done: "#4c8c2b",
};

/* Interview type options for filter chips */
const TYPE_FILTERS = [
  { label: "2 ans", value: "2" },
  { label: "3 ans", value: "3" },
  { label: "6 ans", value: "6" },
];

/**
 * @param {Object} props
 * @param {Array}  props.data - interviews list
 * @param {Function} props.onRowClick - called with interview row data
 * @param {Array}  props.statusOptions - [{label, value, color}, ...] from VentrSlice
 * @param {Array}  props.typeOptions - [{label, value}, ...] from VentrSlice
 * @param {boolean} props.showStatusFilter - whether to show status filter chips (upcoming view only)
 */
export default function MobileInterviewsList({
  data,
  onRowClick,
  statusOptions,
  typeOptions,
  showStatusFilter,
}) {
  const [searchText, setSearchText] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [page, setPage] = useState(1);

  /* ---- Status filter toggle ---- */
  const toggleStatus = (statusValue) => {
    setSelectedStatuses((prev) =>
      prev.includes(statusValue)
        ? prev.filter((s) => s !== statusValue)
        : [...prev, statusValue]
    );
    setPage(1);
  };

  /* ---- Type filter toggle ---- */
  const toggleType = (typeValue) => {
    setSelectedTypes((prev) =>
      prev.includes(typeValue)
        ? prev.filter((t) => t !== typeValue)
        : [...prev, typeValue]
    );
    setPage(1);
  };

  /* ---- Filtering logic ---- */
  const filteredData = useMemo(() => {
    let list = data || [];

    // Text search across key fields
    if (searchText.trim()) {
      const terms = searchText.toLowerCase().split(" ").filter(Boolean);
      list = list.filter((row) => {
        const searchStr = [
          row.collab_prenom,
          row.collab_nom,
          row.team_name,
          row.type,
          row.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchStr.includes(term));
      });
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      list = list.filter((row) => selectedStatuses.includes(row.status));
    }

    // Type filter
    if (selectedTypes.length > 0) {
      list = list.filter((row) => selectedTypes.includes(row.type));
    }

    return list;
  }, [data, searchText, selectedStatuses, selectedTypes]);

  /* ---- Reset page on filter change ---- */
  React.useEffect(() => {
    setPage(1);
  }, [searchText, selectedStatuses, selectedTypes]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* ---- Helpers ---- */
  const getStatusLabel = (statusValue) => {
    const opt = (statusOptions || []).find((o) => o.value === statusValue);
    return opt ? opt.label : statusValue;
  };

  const getStatusColor = (statusValue) => {
    return STATUS_COLORS[statusValue] || "#888";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return moment(dateStr).format("DD/MM/YYYY");
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return moment(dateStr).format("DD/MM/YYYY HH:mm");
  };

  /* ---- Available status options for filter (exclude 'done' in upcoming view) ---- */
  const filterableStatuses = useMemo(() => {
    if (!statusOptions) return [];
    return showStatusFilter
      ? statusOptions.filter((o) => o.value !== "done")
      : [];
  }, [statusOptions, showStatusFilter]);

  const filterableTypes = typeOptions || TYPE_FILTERS;

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: 0,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover fieldset": {
              borderColor: "rgba(0,0,0,0.15)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "warning.main",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
            },
          },
        }}
      />

      {/* Status filter chips */}
      {filterableStatuses.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            mb: 1,
          }}
        >
          {filterableStatuses.map((opt) => {
            const isActive = selectedStatuses.includes(opt.value);
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => toggleStatus(opt.value)}
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  height: 28,
                  borderRadius: "10px",
                  transition: TRANSITION_SMOOTH,
                  color: isActive ? "#fff" : opt.color,
                  bgcolor: isActive ? opt.color : "transparent",
                  border: `1.5px solid ${opt.color}`,
                  "&:hover": {
                    bgcolor: isActive
                      ? opt.color
                      : `${opt.color}15`,
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Type filter chips */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          mb: 1,
        }}
      >
        {filterableTypes.map((opt) => {
          const isActive = selectedTypes.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleType(opt.value)}
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                height: 28,
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                color: isActive ? "#fff" : "rgba(0,0,0,0.65)",
                bgcolor: isActive ? "rgb(255,144,21)" : "transparent",
                border: isActive
                  ? "1.5px solid rgb(255,144,21)"
                  : "1.5px solid rgba(0,0,0,0.15)",
                "&:hover": {
                  bgcolor: isActive
                    ? "rgb(255,144,21)"
                    : "rgba(255,144,21,0.06)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            />
          );
        })}
      </Box>

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 500,
          letterSpacing: "0.02em",
          opacity: 0.7,
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.12)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedData.map((interview, index) => (
          <Fade in key={interview.id || index} timeout={200 + index * 40}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow:
                  "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                transition:
                  "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:active": {
                  transform: "scale(0.98)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                },
              }}
            >
              <CardActionArea
                onClick={() => onRowClick(interview)}
                sx={{ p: 0 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Top row: name + status chip */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {/* Avatar circle */}
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          bgcolor: getStatusColor(interview.status),
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          flexShrink: 0,
                          opacity: 0.85,
                        }}
                      >
                        {(interview.collab_prenom?.[0] || "").toUpperCase()}
                        {(interview.collab_nom?.[0] || "").toUpperCase()}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            lineHeight: 1.3,
                            letterSpacing: "-0.01em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {interview.collab_prenom} {interview.collab_nom}
                        </Typography>
                        {interview.team_name && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.75rem",
                              opacity: 0.75,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <GroupsOutlinedIcon sx={{ fontSize: "0.85rem" }} />
                            {interview.team_name}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status chip */}
                    {interview.status && (
                      <Chip
                        label={getStatusLabel(interview.status)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          height: 24,
                          borderRadius: "8px",
                          color: "#fff",
                          bgcolor: getStatusColor(interview.status),
                          flexShrink: 0,
                          ml: 1,
                        }}
                      />
                    )}
                  </Box>

                  {/* Info row: type + dates */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 0.75,
                      mt: 0.5,
                    }}
                  >
                    {/* Interview type chip */}
                    {interview.type && (
                      <Chip
                        label={`${interview.type} ans`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.7rem",
                          height: 24,
                          borderRadius: "8px",
                          fontWeight: 600,
                          borderColor: "rgb(255,144,21)",
                          color: "rgb(255,144,21)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}

                    {/* Planned date */}
                    {interview.planned_date && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <EventNoteOutlinedIcon
                          sx={{
                            fontSize: "0.9rem",
                            color: "text.secondary",
                            opacity: 0.6,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {formatDateTime(interview.planned_date)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Theoretical date (secondary info) */}
                  {interview.theoretical_date && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.7rem",
                        opacity: 0.55,
                        mt: 0.5,
                        display: "block",
                      }}
                    >
                      <Langs str="INTERVIEW_THEORETICAL_DATE" />:{" "}
                      {formatDate(interview.theoretical_date)}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ opacity: 0.5 }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
